import { useFragment } from "@/generated";
import {
  CreateVocabCardDocument,
  GenerateVocabCardDocument,
  VocabCardFieldsFragmentDoc,
} from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";

interface ManualCreationInput {
  word: string;
  definition: string;
  example: string;
  partOfSpeech: string;
  imageFile: File | null;
  audioFile: File | null;
}

export const useAddVocabCard = () => {
  const [generateVocabCardMutation, { loading: generateLoading }] = useMutation(
    GenerateVocabCardDocument,
    {
      update(cache, { data }) {
        if (!data?.generateVocabCard) return;

        const newVocabCard = useFragment(VocabCardFieldsFragmentDoc, data.generateVocabCard);

        const id = cache.identify(newVocabCard);

        if (!id) return;

        // Write the new vocab card to cache
        cache.writeFragment({
          id,
          fragment: VocabCardFieldsFragmentDoc,
          data: newVocabCard,
        });

        cache.modify({
          fields: {
            getAllVocabCards(existingData, { storeFieldName }) {
              if (!storeFieldName.includes('"searchText":null') || !existingData?.items) {
                return existingData;
              }

              const newRef = { __ref: cache.identify(newVocabCard) };

              return {
                ...existingData,
                items: [newRef, ...existingData.items],
                total: existingData.total + 1,
              };
            },
          },
        });
      },
    }
  );

  const [createVocabCardMutation, { loading: createLoading }] = useMutation(
    CreateVocabCardDocument,
    {
      update(cache, { data }) {
        if (!data?.createVocabCard) return;

        const newVocabCard = useFragment(VocabCardFieldsFragmentDoc, data.createVocabCard);

        const id = cache.identify(newVocabCard);

        if (!id) return;

        // Write the new vocab card to cache
        cache.writeFragment({
          id,
          fragment: VocabCardFieldsFragmentDoc,
          data: newVocabCard,
        });

        cache.modify({
          fields: {
            getAllVocabCards(existingData, { storeFieldName }) {
              if (!storeFieldName.includes('"searchText":null') || !existingData?.items) {
                return existingData;
              }

              const newRef = { __ref: cache.identify(newVocabCard) };

              return {
                ...existingData,
                items: [newRef, ...existingData.items],
                total: existingData.total + 1,
              };
            },
          },
        });
      },
    }
  );

  const addVocabCard = async (
    word: string,
    type: "generated" | "manual",
    input?: ManualCreationInput
  ) => {
    return withToast(
      async () => {
        if (type === "generated") {
          await generateVocabCardMutation({
            variables: {
              word,
            },
          });
        }

        if (type === "manual" && input) {
          await createVocabCardMutation({
            variables: {
              input: {
                word: input.word,
                definition: input.definition,
                example: input.example,
                partOfSpeech: input.partOfSpeech,
              },
              image: input.imageFile,
              audio: input.audioFile,
            },
          });
        }
      },
      {
        success: "Succesfully added new vocabulary card",
        loading: "Creating vocabulary card...",
        error: "Failed to create new vocabulary card",
      },
      "Vocab-card-add-toast"
    );
  };

  return {
    loading: createLoading || generateLoading,
    addVocabCard,
  };
};
